import { Button } from "@/components/ui/button";
import { setTokenRefresher } from "@/providers/api-provider";
import { SpanStatusCode, trace } from "@opentelemetry/api";
import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router";
import { SpinnerInfinity } from "spinners-react";

import { IstariUserProfile, useUserStore } from "../stores/user-store";

const tracer = trace.getTracer("customer-portal-ui");

// Define the shape of the user profile

interface UserContextType {
  user: IstariUserProfile;
  setUser: (user: IstariUserProfile) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const user = useUserStore.use.user();
  const setUser = useUserStore.use.setUser();
  const isAuthenticated = useUserStore.use.isAuthenticated();
  const setIsAuthenticated = useUserStore.use.setIsAuthenticated();
  const setToken = useUserStore.use.setToken();
  const fetchOrgUsers = useUserStore.use.fetchOrgUsers();
  const orgUsers = useUserStore.use.orgUsers();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Set up token refresher function
  useEffect(() => {
    setTokenRefresher(async () => {
      const span = tracer.startSpan("auth.token_refresh");
      try {
        const user = await auth.signinSilent();
        if (user?.access_token) {
          setToken(user.access_token);
          // Also update the user profile so that permissions/roleKeys in the store reflect
          // the latest token claims (e.g. after a role change that triggered a 403).
          setUser(user.profile as IstariUserProfile);
          span.setAttribute("refresh.success", true);
          span.addEvent("token_refreshed");
          span.end();
          return user.access_token;
        }
        span.setAttribute("refresh.success", false);
        span.setAttribute("refresh.reason", "no_access_token");
        span.end();
        return null;
      } catch (error) {
        span.setAttribute("refresh.success", false);
        span.setAttribute("error.message", error instanceof Error ? error.message : String(error));
        span.setStatus({ code: SpanStatusCode.ERROR });
        span.end();
        console.error("Token refresh failed:", error);
        return null;
      }
    });
  }, [auth, setToken, setUser]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const span = tracer.startSpan("flow.user_session_init", {
        attributes: {
          "user.id": auth.user.profile.sub || "",
          "user.has_default_org": !!auth.user.profile.defaultOrg,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          "user.org_count": (auth.user.profile as any).organizations?.length || 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          "user.is_super_admin": (auth.user.profile as any).superAdmin || false,
          "auth.token_expires_at": auth.user.expires_at || 0,
        },
      });

      try {
        localStorage.setItem("has-visited", "true");
        setUser(auth.user?.profile as IstariUserProfile);
        setIsAuthenticated(true);
        setToken(auth.user?.access_token || "");
        span.addEvent("user_profile_loaded");

        fetchOrgUsers()
          .then(() => {
            span.addEvent("org_users_fetched");
            span.end();
          })
          .catch((error) => {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: error instanceof Error ? error.message : String(error),
            });
            span.end();
          });

        const savedPath = (auth.user?.state as { from?: string })?.from;

        if (window.location.search) {
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, document.title, url.toString());
        }

        if (savedPath && savedPath !== "/" && savedPath !== window.location.pathname) {
          navigate(savedPath, { replace: true });
        }
      } catch (error) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : String(error),
        });
        span.end();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.isAuthenticated,
    // Intentionally omitting auth.user?.access_token and auth.user?.profile:
    // token refresh (signinSilent) updates auth.user, which would re-trigger fetchOrgUsers
    // in an infinite loop.  The refreshTokenFn already calls setToken/setUser directly, so
    // the store stays in sync without re-running this entire init effect on every refresh.
    fetchOrgUsers,
    setIsAuthenticated,
    setToken,
    setUser,
  ]);

  useEffect(() => {
    console.info("Org Users in store:", orgUsers);
  }, [orgUsers]);

  // signinRedirect must run in a useEffect (not during render) to avoid updating
  // AuthProvider state while UserProvider is rendering, which causes a React error.
  // Deferring to an effect also lets the router loader's redirect to /welcome complete
  // before any OIDC redirect fires for first-time visitors.
  const needsRedirect = !auth.isAuthenticated && !auth.isLoading && !auth.error;

  React.useEffect(() => {
    if (needsRedirect) {
      auth.signinRedirect({
        state: { from: location.pathname + location.search },
      });
    }
  }, [needsRedirect, auth, location.pathname, location.search]);

  if (auth.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <SpinnerInfinity
          size={70}
          thickness={180}
          speed={149}
          color="rgba(78, 172, 57, 1)"
          secondaryColor="rgba(0, 0, 0, 1)"
        />
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="mr-4">Error: {auth.error.message}</div>
        <Button variant="outline" onClick={() => auth.signinRedirect()}>
          Click here to login again
        </Button>
      </div>
    );
  }

  if (needsRedirect) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ user, setUser, isAuthenticated, setIsAuthenticated }}>
      {children}
    </UserContext.Provider>
  );
};
